import { Timestamp } from '@bufbuild/protobuf'
import {
  createConnectQueryKey,
  disableQuery,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import { EventLocationType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/event_location_type_pb'
import { Booking } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/booking_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { EventTypeSchedule } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_schedule_pb'
import { GroupClassAttendance } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_attendance_pb'
import { GroupClass } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/group_class_pb'
import { SiteContact } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/site_contact_pb'
import { UserProfile } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/user_profile_pb'
import { BookingItemRecord } from '@fingertip/creator-proto/gen/fingertip/creator/booking/v1/booking_pb'
import {
  createBookingPublic,
  getBookingPublic,
  updateBooking,
} from '@fingertip/creator-proto/gen/fingertip/creator/booking/v1/booking-BookingService_connectquery'
import { DaySlots } from '@fingertip/creator-proto/gen/fingertip/creator/event_type/v1/event_type_pb'
import { bookGroupClassPublic } from '@fingertip/creator-proto/gen/fingertip/creator/group_class/v1/group_class-GroupClassService_connectquery'
import { getGroupClassAttendancePublic } from '@fingertip/creator-proto/gen/fingertip/creator/group_class_attendance/v1/group_class_attendance-GroupClassAttendanceService_connectquery'
import { SiteAvailabilityByDayTimeSlot } from '@fingertip/creator-proto/gen/fingertip/creator/nylas/v1/nylas_pb'
import { GetStoreConfigPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_config/v1/store_config_pb'
import {
  applyStoreCouponToAttendancePublic,
  applyStoreCouponToBookingPublic,
  getStoreCouponPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/store_coupon/v1/store_coupon-StoreCouponService_connectquery'
import { getStripeBookingPaymentAmountPublic } from '@fingertip/creator-proto/gen/fingertip/creator/store_payment/v1/store_payment-StorePaymentService_connectquery'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { addMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isValidPhoneNumber } from 'react-phone-number-input/min'
import { toast } from 'sonner'
import { z } from 'zod'

import { BookerEventHeader } from '@/components/booking/booker/header/booker-event-header'
import { BookerBottomNav } from '@/components/booking/booker/nav/booker-bottom-nav'
import { BookerTopNav } from '@/components/booking/booker/nav/booker-top-nav'
import { useBookerNav } from '@/components/booking/booker/nav/use-booker-nav'
import { BookerStageAttendanceSuccess } from '@/components/booking/booker/stages/booker-stage-attendance-success'
import { BookerStageAvailableSlots } from '@/components/booking/booker/stages/booker-stage-available-slots'
import { BookerStageBooking } from '@/components/booking/booker/stages/booker-stage-booking'
import { BookerStageBookingSuccess } from '@/components/booking/booker/stages/booker-stage-booking-success'
import { BookerStageDatePicker } from '@/components/booking/booker/stages/booker-stage-date-picker'
import { BookerStageEventTypePicker } from '@/components/booking/booker/stages/booker-stage-event-type-picker'
import { BookerStageMemberPicker } from '@/components/booking/booker/stages/booker-stage-member-picker'
import { BookerStagePayment } from '@/components/booking/booker/stages/booker-stage-payment'
import { CREATE_BOOKING_REFETCH_DELAY } from '@/components/booking/constants'
import {
  eventLocationTypeMappingName,
  mappingNameToEventLocation,
} from '@/components/booking/mappers'
import { LocationTypeEnum } from '@/components/booking/types'
import { useBookingSelection } from '@/lib/hooks/use-booking-selection'
import { useQueryParamOnce } from '@/lib/hooks/use-query-params-once'
import { useToken } from '@/lib/hooks/use-token'
import { BookingStage } from '@/lib/stores/booker-store'
import { RootStoreContext } from '@/lib/stores/root-store'

import { getLanguage } from '../../blocks/calendar/utils'
import { useNonEmptyScheduleDays } from '../utils'

const createBookerSchema = ({ phoneRequired }: { phoneRequired: boolean }) =>
  z.object({
    name: z.string().min(1, 'Name is required.'),
    email: z
      .string()
      .email({ message: 'Invalid email address' })
      .min(1, 'Email address is required.'),
    phone: phoneRequired
      ? z
          .string()
          .min(1, 'Phone number is required')
          .refine(isValidPhoneNumber, { message: 'Invalid phone number' })
      : z
          .string()
          .refine(isValidPhoneNumber, { message: 'Invalid phone number' })
          .or(z.literal('')),
    locationType: LocationTypeEnum,
    locationValue: z.string().optional(),
    guests: z.array(z.string().email({ message: 'Invalid email address' })),
    notes: z.string().optional(),
    timeZone: z.string(),
    language: z.string().optional(),
    formResponseId: z.string().optional(),
    couponCode: z.string().optional(),
  })

export type CustomerBookerSchema = z.infer<
  ReturnType<typeof createBookerSchema>
>

type Props = {
  selectedEventType?: EventType
  eventTypes?: EventType[]
  eventTypeSchedules?: EventTypeSchedule[]
  members?: UserProfile[]
  booking?: Booking
  bookingItems?: BookingItemRecord[]
  groupClass?: GroupClass
  groupClassAttendance?: GroupClassAttendance
  groupClassAttendanceContact?: SiteContact
  groupClassEventType?: EventType
  storeConfigData: GetStoreConfigPublicResponse
  siteSlug: string
  source: string
  slotsByDay?:
    | {
        [key: string]: DaySlots
      }
    | undefined
  availabilitySlots?: SiteAvailabilityByDayTimeSlot[]
  siteConnectionCount: number
  onRootBack?: () => void
  isLoading?: boolean
  stripeConnectedAccountId?: string
  currentUser?: UserProfile
  isEmbedMode?: boolean
}

export const Booker = observer(
  ({
    selectedEventType,
    eventTypes,
    eventTypeSchedules,
    members,
    slotsByDay,
    booking,
    bookingItems,
    groupClass,
    groupClassAttendance,
    groupClassAttendanceContact,
    groupClassEventType,
    source,
    availabilitySlots,
    siteConnectionCount = 0,
    onRootBack,
    siteSlug,
    isLoading,
    storeConfigData,
    stripeConnectedAccountId,
    currentUser,
    isEmbedMode,
  }: Props) => {
    const { t } = useTranslation()
    const { callOptions } = useToken()
    const {
      bookerStore: {
        stage,
        selectedTimeslot,
        selectedDate,
        setSelectedDate,
        setMonth,
        setStage,
        bookingId,
        setBookingId,
        setEventTypeId,
        setSelectedTimeslot,
        selectedGroupClassId,
        setAttendanceId,
        selectedUsers,
        selectedLocation,
        timeZone,
        bookingItemsData,
        addBookingItem,
        setMemberOptions,
        setSelectedUserId,
        setSelectionToken,
        customPrice,
        customLength,
        groupWaitlisted,
      },
      bookingCheckoutStore: { targetBookingId },
    } = useContext(RootStoreContext)

    const storeConfig = storeConfigData?.config

    const bookingSelection = useBookingSelection(bookingItemsData)

    useQueryParamOnce(
      [
        'stage',
        'bookingId',
        'attendanceId',
        'eventTypeId',
        'selection',
        'assigneeId',
      ],
      (value) => {
        const [
          stageValue,
          bookingIdValue,
          attendanceIdValue,
          eventTypeIdValue,
          selectionValue,
        ] = value as (string | null)[]

        if (stageValue) {
          setStage(stageValue.toUpperCase() as BookingStage)
        }
        if (bookingIdValue) {
          setBookingId(bookingIdValue)
        }
        if (attendanceIdValue) {
          setAttendanceId(attendanceIdValue)
        }
        if (eventTypeIdValue) {
          setEventTypeId(eventTypeIdValue)
        }
        if (selectionValue) {
          setSelectionToken(selectionValue)
        }
      },
      false,
    )

    const queryClient = useQueryClient()
    const { previousStage, nextStage } = useBookerNav()
    const nonEmptyScheduleDays = useNonEmptyScheduleDays(slotsByDay)
    const [appliedCoupon, setAppliedCoupon] = React.useState<
      string | undefined
    >(undefined)

    const form = useForm<CustomerBookerSchema>({
      resolver: zodResolver(
        createBookerSchema({
          phoneRequired: selectedLocation?.type === 'ATTENDEE_PHONE',
        }),
      ),
      mode: 'all',
      defaultValues: {
        name: booking?.response?.name || '',
        email: booking?.response?.email || '',
        phone: booking?.response?.phone || '',
        locationValue: booking?.response?.locationValue || '',
        locationType: eventLocationTypeMappingName(
          booking?.response?.locationType || EventLocationType.UNSPECIFIED,
        ) as z.infer<typeof LocationTypeEnum>,
        guests: booking?.response?.guests || [],
        notes: booking?.response?.notes || '',
        timeZone,
        language: getLanguage(),
        formResponseId: booking?.formResponseId || '',
        couponCode: undefined,
      },
    })

    const watchCouponCode = form.watch('couponCode')
    const watchTimezone = form.watch('timeZone')

    const { data: bookingPaymentData } = useQuery(
      getStripeBookingPaymentAmountPublic,
      !bookingSelection || bookingSelection.bookingItems.length === 0
        ? disableQuery
        : {
            siteSlug,
            eventTypes: bookingSelection.eventTypes,
            couponCode: watchCouponCode,
            customPrice: customPrice ?? undefined,
          },
      {
        callOptions,
      },
    )

    const { data: storeCouponData } = useQuery(
      getStoreCouponPublic,
      !watchCouponCode
        ? disableQuery
        : {
            siteSlug,
            couponCode: watchCouponCode,
          },
      {
        callOptions,
      },
    )

    useEffect(() => {
      form.setValue('timeZone', timeZone)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeZone])

    // TODO: Make sure this triggers when switching between location types
    useEffect(() => {
      // Only apply changes when CREATING a booking
      if (!booking && selectedLocation) {
        form.setValue('locationType', selectedLocation?.type)
        form.setValue('locationValue', selectedLocation?.value)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation])

    const mutationApplyCouponToBooking = useMutation(
      applyStoreCouponToBookingPublic,
      {
        callOptions,
        onSuccess: (_, variables) => {
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey(getBookingPublic, {
              bookingId: variables.bookingId,
            }),
          })

          setStage('SUCCESS')
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )

    const mutationApplyCouponToAttendance = useMutation(
      applyStoreCouponToAttendancePublic,
      {
        callOptions,
        onSuccess: (_, variables) => {
          queryClient.invalidateQueries({
            queryKey: createConnectQueryKey(getGroupClassAttendancePublic, {
              groupClassAttendanceId: variables.attendanceId,
            }),
          })

          setStage('SUCCESS')
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )

    const mutationCreate = useMutation(createBookingPublic, {
      callOptions,
      onSuccess: (data, variables) => {
        toast.success(t('booking_scheduled'))
        setBookingId(data.bookingId)

        setTimeout(() => {
          form.setValue('name', variables.response?.name || '')
          form.setValue('email', variables.response?.email || '')
          form.setValue('guests', variables.response?.guests || [])
          form.setValue('notes', variables.response?.notes || '')
          form.setValue('phone', variables.response?.phone || '')
          form.setValue('formResponseId', variables.formResponseId || '')
          form.setValue(
            'locationType',
            variables.response?.locationType
              ? eventLocationTypeMappingName(variables.response?.locationType)
              : 'UNSPECIFIED',
          )
          form.setValue(
            'locationValue',
            variables.response?.locationValue || '',
          )
        }, 0)

        setTimeout(async () => {
          // Invalidate so that it refreshes
          await queryClient.invalidateQueries({
            queryKey: createConnectQueryKey(getBookingPublic, {
              bookingId: data.bookingId,
            }),
          })
        }, CREATE_BOOKING_REFETCH_DELAY)

        if (appliedCoupon && bookingPaymentData?.totalAmountInCents === 0) {
          mutationApplyCouponToBooking.mutate({
            siteSlug,
            bookingId: data.bookingId,
            couponCode: appliedCoupon,
          })
        } else {
          setStage('SUCCESS')
        }
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })

    const mutationUpdate = useMutation(updateBooking, {
      callOptions,
      onSuccess: (_, variables) => {
        toast.success(t('booking_rescheduled'))
        setStage('SUCCESS')
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey(getBookingPublic, {
            bookingId: variables.bookingId,
          }),
        })
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })

    const mutationBookGroup = useMutation(bookGroupClassPublic, {
      callOptions,
      onSuccess: (data) => {
        toast.success(t('booking_scheduled'))
        if (data.attendance?.id) setAttendanceId(data.attendance.id)

        if (appliedCoupon && bookingPaymentData?.totalAmountInCents === 0) {
          mutationApplyCouponToAttendance.mutate({
            siteSlug,
            attendanceId: data?.attendance?.id,
            couponCode: appliedCoupon,
          })
        } else {
          setStage('SUCCESS')
        }
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })

    const finalStartTime = useMemo(() => {
      return selectedTimeslot
        ? Timestamp.fromDate(toZonedTime(selectedTimeslot, watchTimezone))
        : undefined
    }, [selectedTimeslot, watchTimezone])

    const finalEndTime = useMemo(() => {
      const length = customLength || bookingSelection?.bookingItemsLength || 0

      return selectedTimeslot
        ? Timestamp.fromDate(
            toZonedTime(addMinutes(selectedTimeslot, length), watchTimezone),
          )
        : undefined
    }, [
      bookingSelection?.bookingItemsLength,
      customLength,
      selectedTimeslot,
      watchTimezone,
    ])

    const submitHandler = useCallback(
      async (input: CustomerBookerSchema) => {
        if (!bookingSelection) {
          return
        }

        if (groupWaitlisted) {
          // Waitlist the user
          // if the attendance type is GROUP, skip creating the actual booking object
          mutationBookGroup.mutate({
            siteSlug,
            targetAttendanceId: targetBookingId,
            groupClassId: selectedGroupClassId
              ? selectedGroupClassId
              : undefined,
            email: input.email,
            name: input.name,
            notes: input.notes,
            timeZone: input.timeZone,
            formResponseId: input.formResponseId,
            phone: input.phone,
          })
          return
        }

        if (
          [AttendanceType.GROUP, AttendanceType.EVENT].includes(
            bookingSelection.bookingItemsAttendanceType,
          )
        ) {
          // if the attendance type is GROUP, skip creating the actual booking object
          mutationBookGroup.mutate({
            siteSlug,
            targetAttendanceId: targetBookingId,
            groupClassId: selectedGroupClassId
              ? selectedGroupClassId
              : undefined,
            email: input.email,
            name: input.name,
            notes: input.notes,
            timeZone: input.timeZone,
            formResponseId: input.formResponseId,
            phone: input.phone,
          })
          return
        }

        if (bookingId) {
          mutationUpdate.mutate({
            ...input,
            bookingId,
            startTime: finalStartTime,
            endTime: finalEndTime,
            assignmentOptions:
              siteConnectionCount === 0
                ? undefined
                : selectedUsers ?? undefined,
          })

          return
        }

        mutationCreate.mutate({
          siteSlug,
          targetBookingId: targetBookingId ?? undefined,
          ...input,
          startTime: finalStartTime,
          endTime: finalEndTime,
          response: {
            name: input.name,
            email: input.email,
            phone: input.phone,
            locationType: mappingNameToEventLocation(input.locationType),
            locationValue: input.locationValue,
            notes: input.notes,
            guests: input.guests,
            timeZone: input.timeZone,
          },
          formResponseId: input.formResponseId,
          assignmentOptions:
            siteConnectionCount === 0 ? undefined : selectedUsers ?? undefined,
          source,
          eventTypes: bookingSelection.eventTypes,
          customPrice: customPrice ?? undefined,
          customLength: customLength ?? undefined,
        })
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [bookingId, mutationCreate, mutationUpdate, selectedTimeslot, source],
    )

    const usingHeader = useMemo(() => {
      return [
        'MEMBER_PICKER',
        'DATE_PICKER',
        'AVAILABLE_SLOTS',
        'FORM',
        'PAYMENT',
      ].includes(String(stage))
    }, [stage])

    const slotColor = useMemo(() => {
      if (bookingItemsData.length === 1) {
        return bookingItemsData[0].eventType.color
      } else {
        return '#000'
      }
    }, [bookingItemsData])

    useEffect(() => {
      if (selectedEventType && bookingItemsData.length === 0) {
        addBookingItem(selectedEventType, 1)
        setStage('MEMBER_PICKER')
      }
    }, [addBookingItem, bookingItemsData, selectedEventType, setStage])

    useEffect(() => {
      if (members) {
        const memberIds = members.map((member) => member.id)
        setMemberOptions(memberIds)
        if (memberIds.length === 1) {
          setSelectedUserId(memberIds[0])
        } else {
          setSelectedUserId(null)
        }
      }
    }, [members, setMemberOptions, setSelectedUserId])

    return (
      <div className="flex flex-col">
        {/* SECTION - Heading */}
        {stage !== 'SUCCESS' && (
          <BookerTopNav onRootBack={onRootBack} previousStage={previousStage} />
        )}

        {/* SECTION - Content */}
        <div className="flex flex-col gap-4 p-1">
          {/* Event header */}
          {usingHeader && (
            <BookerEventHeader
              members={members}
              storeConfig={storeConfig}
              stage={stage}
              bookingSelection={bookingSelection}
              depositInCents={bookingPaymentData?.depositAmountInCents}
              amountTotalInCents={bookingPaymentData?.totalAmountInCents}
            />
          )}

          {/* Stage - Select Event type */}
          {(stage === 'SERVICES' || !stage) && (
            <BookerStageEventTypePicker
              storeConfig={storeConfig}
              isEmbedMode={isEmbedMode}
              eventTypes={eventTypes}
              eventTypeSchedules={eventTypeSchedules}
              nextStage={nextStage}
            />
          )}

          {stage === 'MEMBER_PICKER' && bookingSelection && (
            <BookerStageMemberPicker
              storeConfig={storeConfig}
              members={members}
              nextStage={nextStage}
            />
          )}

          {/* Stage - Select a date */}
          {stage === 'DATE_PICKER' && bookingSelection && (
            <BookerStageDatePicker
              onChange={(date: Date | null) => {
                setSelectedDate(date)
                setSelectedTimeslot(null)
                setStage(nextStage)
              }}
              onMonthChange={(date: Date) => {
                setSelectedDate(null)
                setMonth(date)
                setSelectedTimeslot(null)
              }}
              includedDates={nonEmptyScheduleDays}
              slotsByDay={slotsByDay}
              slotColor={slotColor}
              selected={selectedDate}
              usesGroupClass={[
                AttendanceType.GROUP,
                AttendanceType.EVENT,
              ].includes(bookingSelection.bookingItemsAttendanceType)}
            />
          )}

          {/* Stage - Select an available time slot */}
          {stage === 'AVAILABLE_SLOTS' && bookingSelection && (
            <BookerStageAvailableSlots
              slotsByDay={slotsByDay}
              availabilitySlots={availabilitySlots}
              bookingSelection={bookingSelection}
              isLoading={isLoading}
              nextStage={nextStage}
            />
          )}

          {/* Stage - Booking Form */}
          {stage === 'FORM' && bookingSelection && (
            <BookerStageBooking
              storeConfig={storeConfig}
              form={form}
              siteSlug={siteSlug}
              bookingSelection={bookingSelection}
              groupClassId={selectedGroupClassId || undefined}
              submitHandler={submitHandler}
              loading={mutationCreate.isPending || mutationUpdate.isPending}
              customPrice={customPrice ?? undefined}
            />
          )}

          {/* Stage - Payment */}
          {stage === 'PAYMENT' && bookingSelection && (
            <BookerStagePayment
              form={form}
              storeConfigData={storeConfigData}
              siteSlug={siteSlug}
              bookingSelection={bookingSelection}
              currentUser={currentUser}
              stripeConnectedAccountId={stripeConnectedAccountId}
              submitHandler={submitHandler}
              appliedCoupon={appliedCoupon}
              setAppliedCoupon={setAppliedCoupon}
              bookingPaymentData={bookingPaymentData}
              storeCouponData={storeCouponData}
            />
          )}

          {/* Stage - Successful */}
          {stage === 'SUCCESS' && (
            <>
              {!!booking && !!bookingItems && (
                <BookerStageBookingSuccess
                  storeConfig={storeConfig}
                  siteSlug={siteSlug}
                  booking={booking}
                  bookingItems={bookingItems}
                />
              )}
              {!!groupClass &&
                !!groupClassAttendance &&
                !!groupClassAttendanceContact &&
                !!groupClassEventType && (
                  <BookerStageAttendanceSuccess
                    storeConfig={storeConfig}
                    eventType={groupClassEventType}
                    groupClass={groupClass}
                    groupClassAttendance={groupClassAttendance}
                    groupClassAttendanceContact={groupClassAttendanceContact}
                    siteSlug={siteSlug}
                  />
                )}
            </>
          )}
        </div>

        {/* SECTION - Bottom nav */}
        {stage !== 'SUCCESS' && stage !== 'FORM' && (
          <BookerBottomNav nextStage={nextStage} />
        )}
      </div>
    )
  },
)
